import React, { useState } from 'react';

import { DATE_DAYJS_FORMAT } from '@enums/dates';
import { useClickOutside } from '@hooks/index';
import { IconCalendar, IconXboxX } from '@tabler/icons-react';
import { Input, Popover } from 'antd';
import { format } from 'date-fns';
import { fr } from 'date-fns/locale';
import dayjs from 'dayjs';
import { DateRange, DayPicker } from 'react-day-picker';
import { QuickSelection } from './QuickSelection';

type DatePickerRangeProps = {
	renderTrigger?: ({ date, handlerClearInput }: { date: string; handlerClearInput: () => void }) => React.ReactNode;
	onCloseDropdown?: () => void;
	onChange: (value: [string | undefined, string | undefined]) => void;
	value?: [string | undefined, string | undefined];
	showQuickSelection?: boolean;
	disablePast?: boolean;
};

export const DatePickerRange = ({
	showQuickSelection = false,
	renderTrigger,
	disablePast = false,
	onChange,
	value
}: DatePickerRangeProps) => {
	const [range, setRange] = useState<DateRange | undefined>(() => {
		if (!value) return { from: undefined };
		return {
			from: value[0] ? dayjs(value[0]).toDate() : undefined,
			to: value[1] ? dayjs(value[1]).toDate() : undefined
		};
	});
	const [open, setOpen] = useState(false);

	const { ref } = useClickOutside(open, setOpen);

	const parseDate = () => {
		const formattedFromDate = range?.from ? dayjs(range.from).format(DATE_DAYJS_FORMAT.FR_SEMI_LONG) : '';
		const formattedToDate = range?.to ? dayjs(range.to).format(DATE_DAYJS_FORMAT.FR_SEMI_LONG) : '';
		return formattedFromDate && formattedToDate
			? `${formattedFromDate}   >   ${formattedToDate}`
			: `${formattedFromDate}${formattedToDate}`;
	};

	const parsedDate = parseDate();

	const handlerClearInput = () => {
		setRange(undefined);
		onChange([undefined, undefined]);
	};

	return (
		<Popover
			trigger="click"
			open={open}
			content={
				<div ref={ref} className="flex flex-row gap-4 select-none">
					{showQuickSelection && <QuickSelection setRange={setRange} onChange={onChange} />}

					<DayPicker
						navLayout="around"
						mode="range"
						selected={range}
						required
						onSelect={(nextRange: DateRange | undefined, selectedDay?: Date) => {
							setRange((datesRange) => {
								let newRange: DateRange | undefined;
								if (datesRange?.from && datesRange?.to) newRange = { from: selectedDay };
								else if (datesRange?.from && !datesRange?.to)
									newRange = { from: datesRange.from, to: selectedDay };
								else if (!datesRange?.from && !datesRange?.to) newRange = { from: selectedDay };
								else newRange = nextRange as DateRange;

								if (newRange?.from || newRange?.to) {
									onChange([
										newRange?.from ? format(newRange.from, 'yyyy-MM-dd') : undefined,
										newRange?.to ? format(newRange.to, 'yyyy-MM-dd') : undefined
									]);
								}

								if (newRange?.from && newRange?.to && newRange.from.getTime() !== newRange.to.getTime()) {
									setOpen(false);
								}

								return newRange;
							});
						}}
						modifiers={disablePast ? { disabled: { before: dayjs().startOf('day').toDate() } } : undefined}
						locale={fr}
						pagedNavigation
					/>
				</div>
			}
		>
			{renderTrigger && (
				<button type="button" onClick={() => setOpen(true)}>
					{renderTrigger({ date: parsedDate, handlerClearInput })}
				</button>
			)}
			{!renderTrigger && (
				<Input
					readOnly
					onClick={() => setOpen(true)}
					value={parseDate()}
					suffix={
						parseDate().length > 0 ? (
							<button
								onClick={() => handlerClearInput()}
								type="button"
								className="flex cursor-pointer items-center"
							>
								<IconXboxX className="text-slate-400" />
							</button>
						) : (
							<button onClick={() => setOpen(true)} type="button" className="flex cursor-pointer items-center">
								<IconCalendar className="text-slate-400" />
							</button>
						)
					}
				/>
			)}
		</Popover>
	);
};
